import { css } from 'aphrodite';
import * as React from 'react';
import { ErrorMessageViewModel, IAccount } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { AccountSnapshotViewModel } from '../../../viewmodels/AdminViewModels';
import { Button } from '../../../web/components/Button';
import { Modal } from '../../../web/components/Modal';
import { SelectBox } from '../../../web/components/SelectBox';
import { invalidateMonthlyRenewalQueries } from '../../containers/reports/MonthlyRenewalAccounts';
import { styleSheet } from './styles';

enum WillRenewPredictionEnum {
	NotSelected = 'Not Selected',
	Yes = 'Yes',
	No = 'No',
	Downgrade = 'Downgrade (MGR Only)',
}

// Type to represent the selected prediction
type WillRenewPrediction = {
	type: string;
	percentage?: number;
};

type Props = {
	snapshot?: AccountSnapshotViewModel;
	account?: IAccount;
	errorMessagesVM: ErrorMessageViewModel;
	onChangeDispatchOverride?: (selection: string) => void;
};

export const WillRenewPredictionSelect = ({ snapshot, account, errorMessagesVM, onChangeDispatchOverride }: Props) => {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging();

	// Parse the initial value to get type and percentage
	const parseInitialValue = (): WillRenewPrediction => {
		const initialValue =
			snapshot?.updatedWillRenewPrediction ??
			snapshot?.accountModel?.additionalInfo?.willRenewPrediction ??
			(account?.additionalInfo?.willRenewPrediction || WillRenewPredictionEnum.NotSelected);

		// If it's a numeric string, it's a downgrade percentage
		if (/^\d+$/.test(initialValue)) {
			return {
				type: WillRenewPredictionEnum.Downgrade,
				percentage: parseInt(initialValue, 10),
			};
		}

		return { type: initialValue };
	};

	const [prediction, setPrediction] = React.useState<WillRenewPrediction>(parseInitialValue());
	const [downgradePercentage, setDowngradePercentage] = React.useState<string>('');
	const [showDowngradeModal, setShowDowngradeModal] = React.useState<boolean>(false);

	const onSelectedWillRenewPredictionChange = async (selection: string) => {
		if (selection === WillRenewPredictionEnum.Downgrade) {
			setShowDowngradeModal(true);
			return;
		}

		// Not downgrade, store the regular value
		const value = selection === WillRenewPredictionEnum.NotSelected ? '' : selection;
		if (onChangeDispatchOverride) {
			onChangeDispatchOverride(value);
		} else if (snapshot) {
			const response = await userSession.webServiceHelper.callWebServiceAsync(
				`admin/accountSnapshot/${snapshot.id}/willRenewPrediction?value=${value}`,
				'PATCH'
			);
			if (!response.success) {
				errorMessagesVM.pushApiError(response);
				logApiError('SnapshotUpdateRenewalPrediction-Error', response);
				return;
			}
			invalidateMonthlyRenewalQueries();
		} else if (account) {
			const response = await userSession.webServiceHelper.callWebServiceAsync(
				`admin/account/${account.id}/additionalInfo/willRenewPrediction?value=${value}`,
				'PATCH'
			);
			if (!response.success) {
				errorMessagesVM.pushApiError(response);
				logApiError('AccountUpdateRenewalPrediction-Error', response);
				return;
			}
			invalidateMonthlyRenewalQueries();
		}

		setPrediction({ type: selection });
	};

	const handleDowngradeSubmit = async () => {
		const numericValue = parseInt(downgradePercentage, 10);
		if (isNaN(numericValue) || numericValue < 10 || numericValue > 100) {
			// Show validation error
			return;
		}

		// Store just the numeric value
		const apiValue = numericValue.toString();
		if (onChangeDispatchOverride) {
			onChangeDispatchOverride(apiValue);
		} else if (snapshot) {
			const response = await userSession.webServiceHelper.callWebServiceAsync(
				`admin/accountSnapshot/${snapshot.id}/willRenewPrediction?value=${apiValue}`,
				'PATCH'
			);
			if (!response.success) {
				errorMessagesVM.pushApiError(response);
				logApiError('SnapshotUpdateRenewalPrediction-Error', response);
				return;
			}
		} else if (account) {
			const response = await userSession.webServiceHelper.callWebServiceAsync(
				`admin/account/${account.id}/additionalInfo/willRenewPrediction?value=${apiValue}`,
				'PATCH'
			);
			if (!response.success) {
				errorMessagesVM.pushApiError(response);
				logApiError('AccountUpdateRenewalPrediction-Error', response);
				return;
			}
		}

		setPrediction({ type: WillRenewPredictionEnum.Downgrade, percentage: numericValue });
		closeDowngradeModal();
	};

	const closeDowngradeModal = () => {
		setShowDowngradeModal(false);
		setDowngradePercentage('');
	};

	const options = React.useMemo(
		() =>
			Object.values(WillRenewPredictionEnum).map(predictionType => {
				return { value: predictionType, title: predictionType };
			}),
		[]
	);

	return (
		<>
			<div className={css(styleSheet.container)}>
				<SelectBox
					triggerClassName={css(styleSheet.selectTrigger)}
					selectedOption={options.find(option => option.value === prediction.type)}
					onSelectionChanged={selectedOption => onSelectedWillRenewPredictionChange(selectedOption.value)}
					options={options}
					triggerStyles={[styleSheet.selectOption]}
				/>
				{prediction.type === WillRenewPredictionEnum.Downgrade && prediction.percentage && (
					<span className={css(styleSheet.percentageLabel)}>({prediction.percentage}%)</span>
				)}
			</div>

			<Modal
				isOpen={showDowngradeModal}
				onRequestClose={closeDowngradeModal}
				useDefaultHeader={true}
				shouldCloseOnOverlayClick={false}
				name='downgrade-percentage-modal'
			>
				<div className={css(styleSheet.modalContent)}>
					<h3 className={css(styleSheet.modalTitle)}>Enter Renewal Percentage</h3>
					<p className={css(styleSheet.modalInstruction)}>Please enter a number between 10-100:</p>
					<input
						type='number'
						min='10'
						max='100'
						value={downgradePercentage}
						onChange={e => setDowngradePercentage(e.target.value)}
						className={css(styleSheet.input)}
					/>
					<div className={css(styleSheet.modalButtons)}>
						<Button
							kind='primary'
							label='Submit'
							onClick={handleDowngradeSubmit}
							className={css(styleSheet.submitButton)}
						/>
						<Button
							kind='reverse'
							label='Cancel'
							onClick={closeDowngradeModal}
							className={css(styleSheet.cancelButton)}
						/>
					</div>
				</div>
			</Modal>
		</>
	);
};
